import { useState, useEffect } from "react";
import SEO from "../components/SEO";
import { trackEvent, trackServerPageView, trackButtonClick, setupPageTimeTracking } from "../lib/analytics";
import { PHONE_NUMBER, PHONE_TEL } from "../lib/contact";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackServerPageView("/contact", "contact_page_view");
    return setupPageTimeTracking("/contact");
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Failed to send message. Please try again.");
      } else {
        setSubmitted(true);
        trackEvent("generate_lead");
        trackEvent("contact_form_submitted", { lead_type: "contact_form" });
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact — Wholesale Sales & Support"
        description="Contact Premier Pharma for wholesale pricing, account setup, or DSCSA support. Boise, ID. Serving pharmacies, clinics, and veterinary practices nationwide."
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact Premier Pharma",
          url: "https://premierpharma.com/contact",
          mainEntity: {
            "@type": ["Organization", "MedicalOrganization", "LocalBusiness"],
            "@id": "https://premierpharma.com/#organization",
            name: "Premier Pharma",
            telephone: PHONE_NUMBER,
            address: {
              "@type": "PostalAddress",
              streetAddress: "7259 W Franklin Rd",
              addressLocality: "Boise",
              addressRegion: "ID",
              postalCode: "83709",
              addressCountry: "US",
            },
            contactPoint: [{
              "@type": "ContactPoint",
              contactType: "sales",
              telephone: PHONE_NUMBER,
              areaServed: "US",
              availableLanguage: ["English"],
            }],
          },
        }}
      />
      <section className="py-20 bg-gradient-to-br from-background to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <span className="text-accent text-sm font-semibold uppercase tracking-wider">Contact Us</span>
              <h1 className="text-4xl font-bold text-primary mt-2 mb-6">Contact Premier Pharma — Wholesale Sales & Support</h1>
              <p className="text-gray-600 mb-8">Have questions about our products or services? We'd love to hear from you.</p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl" aria-hidden="true">📍</span>
                  <div>
                    <h3 className="font-semibold text-primary">Address</h3>
                    <p className="text-gray-600 text-sm">7259 W Franklin Rd, Boise, Idaho 83709, US</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl" aria-hidden="true">📞</span>
                  <div>
                    <h3 className="font-semibold text-primary">Phone</h3>
                    <a href={PHONE_TEL} className="text-accent hover:underline text-sm font-medium">{PHONE_NUMBER}</a>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              {submitted ? (
                <div role="status" className="text-center py-8">
                  <div className="text-5xl mb-4" aria-hidden="true">✅</div>
                  <h3 className="text-xl font-bold text-primary">Message Sent!</h3>
                  <p className="text-gray-600 mt-2">We'll get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                      {error}
                    </div>
                  )}
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-primary mb-1">Name</label>
                    <input id="contact-name" type="text" required className="input-field" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} disabled={loading} />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-primary mb-1">Email</label>
                    <input id="contact-email" type="email" required className="input-field" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} disabled={loading} />
                  </div>
                  <div>
                    <label htmlFor="contact-company" className="block text-sm font-medium text-primary mb-1">Company</label>
                    <input id="contact-company" type="text" className="input-field" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} disabled={loading} />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-primary mb-1">Message</label>
                    <textarea id="contact-message" rows={4} required className="input-field" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} disabled={loading} />
                  </div>
                  <button type="submit" className="btn-primary w-full" disabled={loading} onClick={() => !loading && trackButtonClick("Send Message", "contact-form")}>
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
